"use client"

import { useEffect, useCallback, useRef, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"

import { useUser, useUserActions, User } from "@/api/users"
// import Fullscreen from "@/components/layout/fullscreen"; // Removed unused import
import { Button } from "@/components/ui/button"
import ProgressBar from "./components/ProgressBar"
import WelcomeUserTypeScreen from "./components/WelcomeUserTypeScreen"
import InformationForm, {
  InformationFormData,
} from "./components/InformationForm"
import { CreateWalletModal } from "@/wallet/create-wallet.modal"

import SuccessScreen from "./components/SuccessScreen"
import * as Sentry from "@sentry/react"
import {
  clearOnboardingSessionStorage,
  parseOnboardingUrlParams,
  getStepTitle,
  getStepNumberByTitle,
} from "./onboarding.utils"
import { readRecentOnboardingTarget, clearOnboardingTarget } from "./onboarding.session"
import { resolveNearestAccessibleStep, clearLocationState, isValidStep, scrubOnboardingLocationState } from "./onboarding.nav"
import {
  useOnboardingActions,
  useCurrentStep,
  useOnboardingUserData,
} from "@/state/stores/onboarding.store"

const STEP_WELCOME = "Welcome"
const STEP_INFORMATION = "Your Information"
const STEP_WALLET = "Wallet Setup"
const STEP_SUCCESS = "All Set!"

interface OnboardingStateData {
  userType: string;
  personalInfo: Partial<InformationFormData>;
  businessInfo: Partial<InformationFormData>;
}

export default function OnboardingScreen() {
  // --- RENDER / MOUNT INSTRUMENTATION ---------------------------------
  const renderCountRef = useRef(0)
  renderCountRef.current += 1
  if (renderCountRef.current === 1) {
    try {
      Sentry.addBreadcrumb({
        category: 'onboarding.lifecycle',
        level: 'info',
        message: 'OnboardingScreen mounted'
      })
    } catch (_) { /* ignore */ }
  } else if (renderCountRef.current % 20 === 0) {
    try {
      Sentry.addBreadcrumb({
        category: 'onboarding.lifecycle',
        level: 'debug',
        message: 'High render count checkpoint',
        data: { renders: renderCountRef.current }
      })
    } catch (_) { /* ignore */ }
  }
  const { user, isLoading: isUserLoading, mutate: rawMutateUser } = useUser()
  // Throttle mutateUser to avoid rapid refetch storms when multiple onboarding side-effects fire close together.
  const mutateThrottleRef = useRef<{ lastCall: number; queued: boolean }>({ lastCall: 0, queued: false })
  const MUTATE_MIN_INTERVAL = 600 // ms

  type MutateUpdater = (oldData: User | undefined) => User
  const mutateUser = useCallback((updater?: MutateUpdater, options?: { revalidate?: boolean }) => {
    const now = Date.now()
    const elapsed = now - mutateThrottleRef.current.lastCall
    if (elapsed >= MUTATE_MIN_INTERVAL) {
      mutateThrottleRef.current.lastCall = now
      rawMutateUser(updater, options)
    } else {
      if (!mutateThrottleRef.current.queued) {
        mutateThrottleRef.current.queued = true
        const delay = MUTATE_MIN_INTERVAL - elapsed + 5
        setTimeout(() => {
          mutateThrottleRef.current.lastCall = Date.now()
          mutateThrottleRef.current.queued = false
          rawMutateUser(updater, options)
          try { // record deferred execution
            Sentry.addBreadcrumb({
              category: 'onboarding.mutateUser',
              level: 'info',
              message: 'Deferred mutateUser executed after throttle window',
              data: { delay }
            })
          } catch (_err) { /* ignore breadcrumb error */ }
        }, delay)
        try { // record throttle
          Sentry.addBreadcrumb({
            category: 'onboarding.mutateUser',
            level: 'debug',
            message: 'mutateUser throttled/deferred',
            data: { elapsed, delay }
          })
        } catch (_err) { /* ignore breadcrumb error */ }
      } else {
        try { // record ignored call
          Sentry.addBreadcrumb({
            category: 'onboarding.mutateUser',
            level: 'debug',
            message: 'mutateUser call ignored (already queued)',
            data: { elapsed }
          })
        } catch (_err) { /* ignore breadcrumb error */ }
      }
    }
  }, [rawMutateUser])
  const { saveOnboardingInfo, completeOnboarding } = useUserActions()
  const navigate = useNavigate()
  const location = useLocation()
  // Use store-backed state/actions to avoid duplicated local state churn
  const {
    setCurrentStep: setCurrentStepSafe,
    setStepTitles,
    updateUserData: updateOnboardingUserData,
  } = useOnboardingActions()
  const currentStep = useCurrentStep()
  const onboardingUserData = useOnboardingUserData()
  // action param now handled via runActionOnce utilities; no direct useParams destructure needed

  const userData = useMemo<OnboardingStateData>(
    () => ({
      userType: onboardingUserData.userType,
      personalInfo: onboardingUserData.personalInfo as Partial<InformationFormData>,
      businessInfo: onboardingUserData.businessInfo as Partial<InformationFormData>,
    }),
    [onboardingUserData],
  )
  const lastResolvedStepRef = useRef<number | null>(null)
  // Debug: track rapid step changes to prevent infinite update loops
  const stepChangeTimestampsRef = useRef<number[]>([])
  const STEP_CHANGE_WINDOW_MS = 1500
  const STEP_CHANGE_MAX_IN_WINDOW = 8

  const recordStepChange = useCallback((from: number, to: number, reason: string) => {
    const now = Date.now()
    stepChangeTimestampsRef.current.push(now)
    // prune old
    stepChangeTimestampsRef.current = stepChangeTimestampsRef.current.filter(ts => now - ts <= STEP_CHANGE_WINDOW_MS)
    const count = stepChangeTimestampsRef.current.length
    try {
      Sentry.addBreadcrumb({
        category: 'onboarding.step',
        level: 'debug',
        message: 'Step change',
        data: { from, to, reason, countInWindow: count }
      })
    } catch (_) { /* ignore */ }
    if (count > STEP_CHANGE_MAX_IN_WINDOW) {
      // Too many changes: capture diagnostic & freeze further automatic changes
      try {
        Sentry.captureMessage('Onboarding step change watchdog triggered', {
          level: 'error',
          extra: { from, to, reason, countInWindow: count }
        })
      } catch (_) { /* ignore */ }
    }
  }, [])
  // console.log('[OnboardingScreen] Initializing/Re-rendering. Initial currentStep state:', 1)

  const stepTitles = useMemo(() => {
    const steps = [STEP_WELCOME, STEP_INFORMATION, STEP_WALLET, STEP_SUCCESS]
    try {
      // Hash / signature for identity churn detection
      const signature = steps.join(' > ')
      interface StepTitlesCacheWindow extends Window { __lastStepTitlesSignature?: string }
      const w = window as StepTitlesCacheWindow
      if (w.__lastStepTitlesSignature !== signature) {
        Sentry.addBreadcrumb({
          category: 'onboarding.steps',
          level: 'info',
          message: 'stepTitles changed',
          data: { signature }
        })
        w.__lastStepTitlesSignature = signature
      }
    } catch (_) { /* breadcrumb add failed */ }
    return steps
  }, [])

  useEffect(() => {
    setStepTitles(stepTitles)
  }, [setStepTitles, stepTitles])

  const totalSteps = stepTitles.length

  useEffect(() => {
    if (currentStep > totalSteps) {
      setCurrentStepSafe(totalSteps, 'clamp-total')
    }
  }, [currentStep, totalSteps, setCurrentStepSafe])

  // Step validation function to ensure proper flow
  const canAccessStep = useCallback((stepNumber: number): boolean => {
    if (!user) return stepNumber === 1 // Only allow step 1 if no user loaded

    const stepTitle = getStepTitle(stepNumber, stepTitles)

    if (stepTitle === STEP_WELCOME) {
      return true
    }

    if (stepTitle === STEP_INFORMATION) {
      // Allow accessing the information step as soon as the user object is loaded.
      // Previously this required first/last name which created a gating paradox
      // (you had to already have the data entered to view the form to enter it),
      // leading to step resolution churn and potential update loops.
      return true
    }

    if (stepTitle === STEP_WALLET) {
      return !!user.firstName && !!user.lastName
    }

    if (stepTitle === STEP_SUCCESS) {
      const hasRequiredProfile = !!user.firstName && !!user.lastName && !!user.hasWallet
      return hasRequiredProfile
    }

    return false
  }, [user, stepTitles])

  // User state consistency validation
  const validateUserState = useCallback((userData: User | undefined): { isValid: boolean; issues: string[] } => {
    const issues: string[] = []
    
    if (!userData) {
      issues.push('User data is missing')
      return { isValid: false, issues }
    }

    // Check for required fields based on current step
    if (currentStep >= 2 && (!userData.firstName || !userData.lastName)) {
      issues.push('Missing required user information (firstName/lastName)')
    }

    if (currentStep >= 3 && !userData.hasWallet) {
      issues.push('Wallet setup required but not completed')
    }

    return { isValid: issues.length === 0, issues }
  }, [currentStep])

  // Validate user state on changes - only run when user object changes, not derived values
  useEffect(() => {
    if (user) {
      const now = Date.now()
      const timeSinceLastValidation = now - lastUserValidationRef.current

      // Debounce validation to prevent excessive runs (max once per second)
      if (timeSinceLastValidation > 1000) {
        lastUserValidationRef.current = now
        const validation = validateUserState(user)
        if (!validation.isValid) {
          // console.warn('[OnboardingScreen] User state validation failed:', validation.issues)
          // Could trigger a user data refresh or show warning
        }
      }
    }
  }, [user, validateUserState]) // validateUserState is stable, only user changes trigger this

  // Refs for cleanup
  const timeoutRefs = useRef<Set<NodeJS.Timeout>>(new Set())

  // Ref to ensure we only consume location.state.step once (prevents re-apply loops)
  const hasConsumedLocationStateRef = useRef(false)
  // Ref to ensure we only consume the ?step= query param once (prevents infinite step bouncing)
  const hasConsumedStepQueryRef = useRef(false)
  const lastLocationSignatureRef = useRef<string | null>(null)

  useEffect(() => {
    const signature = `${location.key ?? 'nokey'}|${location.pathname}|${location.search}`
    if (lastLocationSignatureRef.current !== signature) {
      lastLocationSignatureRef.current = signature
      hasConsumedLocationStateRef.current = false
      hasConsumedStepQueryRef.current = false
    }
  }, [location.key, location.pathname, location.search])

  // Ref to ensure wallet auto-advance only runs once
  const walletAutoAdvancedRef = useRef(false)

  // Ref to track last user validation time for debouncing
  const lastUserValidationRef = useRef<number>(0)
  // Guard to prevent multiple handleNext executions in same event/frame
  const handleNextInFlightRef = useRef(false)

  // Helper function to set timeout with cleanup tracking
  const setTrackedTimeout = useCallback((callback: () => void, delay: number) => {
    const timeoutId = setTimeout(() => {
      timeoutRefs.current.delete(timeoutId)
      callback()
    }, delay)
    timeoutRefs.current.add(timeoutId)
    return timeoutId
  }, [])

  // Cleanup effect to prevent memory leaks
  useEffect(() => {
    const currentTimeouts = timeoutRefs.current
    return () => {
      // Clear all tracked timeouts
      currentTimeouts.forEach(timeoutId => {
        clearTimeout(timeoutId)
      })
      currentTimeouts.clear()
    }
  }, [])

  // Unified step resolution effect (combines previous mount + location effects)
  useEffect(() => {
  const { next, step: stepFromUrl, action: actionParam } = parseOnboardingUrlParams(location.search)
    try {
      Sentry.addBreadcrumb({
        category: 'onboarding.unifiedEffect',
        level: 'debug',
        message: 'Unified step resolution effect fired',
        data: {
          currentStep,
          location: location.pathname + location.search,
          hasConsumedStepQuery: hasConsumedStepQueryRef.current,
          hasConsumedLocationState: hasConsumedLocationStateRef.current,
          nextParam: next,
          stepFromUrl,
          actionParam,
          stepTitles: stepTitles.join('|')
        }
      })
    } catch (_) { /* ignore */ }
    const recentTarget = readRecentOnboardingTarget(2000)

    let desiredStep: number | null = null

    if (!hasConsumedLocationStateRef.current) {
      const state = (location.state ?? null) as Record<string, unknown> | null
      const rawStateStep = state?.targetStep ?? state?.step
      if (typeof rawStateStep !== 'undefined') {
        const parsedFromState = typeof rawStateStep === 'number'
          ? rawStateStep
          : parseInt(String(rawStateStep), 10)
        const targetSource = (state?.targetSource as string | undefined) ?? 'state'

        if (!Number.isNaN(parsedFromState) && isValidStep(parsedFromState, totalSteps)) {
          desiredStep = parsedFromState
          try {
            Sentry.addBreadcrumb({
              category: 'onboarding.stepGuard',
              level: 'info',
              message: 'Consumed step from location.state',
              data: { step: parsedFromState, source: targetSource }
            })
          } catch (_) { /* ignore breadcrumb errors */ }
        } else {
          try {
            Sentry.addBreadcrumb({
              category: 'onboarding.stepGuard',
              level: 'warning',
              message: 'Discarded invalid step from location.state',
              data: { raw: rawStateStep, source: targetSource }
            })
          } catch (_) { /* ignore breadcrumb errors */ }
        }
        hasConsumedLocationStateRef.current = true
        clearLocationState(navigate, location)
      }
    }

    if (desiredStep === null && stepFromUrl && !hasConsumedStepQueryRef.current) {
      const parsed = parseInt(stepFromUrl, 10)
      const isValid = isValidStep(parsed, totalSteps)
      hasConsumedStepQueryRef.current = true
      try {
        Sentry.addBreadcrumb({
          category: 'onboarding.stepGuard',
          level: isValid ? 'info' : 'warning',
          message: isValid ? 'Consumed step query param' : 'Discarded invalid step query param',
          data: { step: parsed, raw: stepFromUrl }
        })
      } catch (_) { /* ignore breadcrumb errors */ }
      try {
        const params = new URLSearchParams(location.search)
        params.delete('step')
        const cleanedState = scrubOnboardingLocationState(location.state as Record<string, unknown> | null | undefined)
        navigate(location.pathname + (params.toString() ? `?${params.toString()}` : ''), {
          replace: true,
          state: cleanedState ?? null,
        })
      } catch (_) {
        // ignore navigation cleanup failures
      }
      if (isValid) {
        desiredStep = parsed
      }
    }

    if (desiredStep === null && recentTarget != null && isValidStep(recentTarget, totalSteps)) {
      desiredStep = recentTarget
    }

    if (desiredStep != null) {
      const resolved = canAccessStep(desiredStep!) ? desiredStep! : resolveNearestAccessibleStep(desiredStep!, canAccessStep)
      try {
        Sentry.addBreadcrumb({
          category: 'onboarding.unifiedEffect',
          level: 'info',
          message: 'Applying desired step via store',
          data: { currentStep, desiredStep, resolved }
        })
      } catch (_) { /* ignore */ }

      if (lastResolvedStepRef.current !== resolved) {
        lastResolvedStepRef.current = resolved
      }
      if (currentStep !== resolved) {
        setCurrentStepSafe(resolved, 'unified-effect')
      }

      // Clear consumed session target to avoid reapplication
      clearOnboardingTarget()
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps  
  }, [location, totalSteps, canAccessStep, navigate, recordStepChange, stepTitles, setCurrentStepSafe]) // INTENTIONALLY excludes currentStep to prevent circular dependency

  const handleNext = useCallback(async () => {
    if (handleNextInFlightRef.current) {
      return
    }
    handleNextInFlightRef.current = true
    try {
      Sentry.addBreadcrumb({ category: 'onboarding.nav', level: 'info', message: 'handleNext invoked', data: { currentStep, totalSteps } })
    } catch (_) { /* breadcrumb failed */ }
    //console.log('[OnboardingScreen] handleNext called. CurrentStep:', currentStep)
    // Clear sessionStorage as internal navigation is now driving the step
    clearOnboardingSessionStorage()

    if (currentStep === totalSteps) {
      try {
        await completeOnboarding()
        await mutateUser(
          (currentUser) =>
            ({
              ...(currentUser || {}),
              onboardingCompleted: true,
            }) as User,
          { revalidate: false },
        )
        // Single revalidation handled intentionally via the updater above; avoid a second immediate mutate
        await new Promise((resolve) => setTimeout(resolve, 1000))
        const nextUrl = new URLSearchParams(location.search).get("next") || "/"
        navigate(nextUrl, { replace: true })
        return
      } catch (error) {
        await mutateUser()
      }
      return
    }

    // Use store's setCurrentStep with built-in watchdog protection
    const nextStepNumber = Math.min(currentStep + 1, totalSteps)
    lastResolvedStepRef.current = nextStepNumber
    setCurrentStepSafe(nextStepNumber, 'handleNext')

    // Mark that we've consumed any location.state.step and clear it to prevent reapplication
    hasConsumedLocationStateRef.current = true
    try {
      // Remove consumed step query param on internal forward navigation to avoid forcing old steps
      const params = new URLSearchParams(location.search)
      const hadStepParam = params.has('step')
      if (hadStepParam) {
        params.delete('step')
        hasConsumedStepQueryRef.current = true
      }
      const originalState = location.state as Record<string, unknown> | null | undefined
      const cleanedState = scrubOnboardingLocationState(originalState)
      if (hadStepParam || cleanedState !== originalState) {
        navigate(location.pathname + (params.toString() ? `?${params.toString()}` : ''), {
          replace: true,
          state: cleanedState ?? null,
        })
      }
    } catch (e) {
      // console.warn('[OnboardingScreen] Failed to clear location.state after handleNext', e)
    }
    // release guard next tick
    setTimeout(() => { handleNextInFlightRef.current = false }, 0)
  }, [currentStep, totalSteps, navigate, location.search, location.pathname, location.state, completeOnboarding, mutateUser, setCurrentStepSafe])

  const handleBack = useCallback(() => {
   // console.log('[OnboardingScreen] handleBack called. CurrentStep:', currentStep)
    try {
      Sentry.addBreadcrumb({ category: 'onboarding.nav', level: 'info', message: 'handleBack invoked', data: { currentStep } })
    } catch (_) { /* breadcrumb failed */ }
    // Clear sessionStorage as internal navigation is now driving the step
    clearOnboardingSessionStorage()

    // Use store's setCurrentStep with built-in watchdog protection
    const backStepNumber = Math.max(currentStep - 1, 1)
    lastResolvedStepRef.current = backStepNumber
    setCurrentStepSafe(backStepNumber, 'handleBack')
    // Mark that we've consumed any location.state.step and clear it to prevent reapplication
    hasConsumedLocationStateRef.current = true
    try {
      // Remove consumed step query param on internal back navigation
      const params = new URLSearchParams(location.search)
      const hadStepParam = params.has('step')
      if (hadStepParam) {
        params.delete('step')
        hasConsumedStepQueryRef.current = true
      }
      const originalState = location.state as Record<string, unknown> | null | undefined
      const cleanedState = scrubOnboardingLocationState(originalState)
      if (hadStepParam || cleanedState !== originalState) {
        navigate(location.pathname + (params.toString() ? `?${params.toString()}` : ''), {
          replace: true,
          state: cleanedState ?? null,
        })
      }
    } catch (e) {
      // console.warn('[OnboardingScreen] Failed to clear location.state after handleBack', e)
    }
  }, [navigate, location.pathname, location.search, location.state, currentStep, setCurrentStepSafe])

  // Removed old LOCATION CHANGE effect (merged into unified effect above)

  useEffect(() => {
    const walletStepNumber = getStepNumberByTitle(STEP_WALLET, stepTitles)
    if (
      walletStepNumber !== -1 &&
      currentStep === walletStepNumber &&
      user?.hasWallet &&
      !walletAutoAdvancedRef.current
    ) {
      walletAutoAdvancedRef.current = true // one-shot guard
      try {
        Sentry.addBreadcrumb({ category: 'onboarding.wallet', level: 'info', message: 'Auto-advance scheduled (wallet ready)' })
      } catch (_) { /* breadcrumb failed */ }
      setTrackedTimeout(() => {
        // Only advance if we're STILL on the wallet step to avoid racing with manual navigation
        if (currentStep === walletStepNumber) {
          try {
            Sentry.addBreadcrumb({ category: 'onboarding.wallet', level: 'info', message: 'Auto-advance executing (wallet)' })
          } catch (_) { /* breadcrumb failed */ }
          handleNext()
        }
      }, 300)
    }
    // Reset guard if user navigates back to an earlier step without a wallet (rare edge-case)
    if (currentStep < walletStepNumber || !user?.hasWallet) {
      walletAutoAdvancedRef.current = false
    }
  }, [user?.hasWallet, currentStep, handleNext, setTrackedTimeout, stepTitles])

  const updateUserDataShared = useCallback(
    (data: {
      personalInfo?: Partial<InformationFormData>;
      businessInfo?: Partial<InformationFormData>;
      userType?: string;
    }) => {
      // console.log('[OnboardingScreen] updateUserDataShared called with:', data)
      updateOnboardingUserData(data)
    },
    [updateOnboardingUserData],
  )

  const renderStepContent = () => {
    if (isUserLoading || !user) return <div>Loading...</div>

    const welcomeUserTypeProps = {
      onNext: handleNext,
      updateUserData: (data: { userType: string }) =>
        updateUserDataShared({ userType: data.userType }),
      user: user,
      userData: userData,
    }

    const commonStepProps = {
      onNext: handleNext,
      updateUserData: updateUserDataShared,
      user: user,
      userData: userData,
      saveOnboardingInfo: saveOnboardingInfo,
      isLoading: isUserLoading,
    }

    const currentStepTitle = getStepTitle(currentStep, stepTitles)

    switch (currentStepTitle) {
      case STEP_WELCOME:
        return <WelcomeUserTypeScreen {...welcomeUserTypeProps} />
      case STEP_INFORMATION:
        return (
          <InformationForm
            userType={userData.userType}
            user={user}
            onNext={handleNext}
            updateUserData={updateUserDataShared}
            saveOnboardingInfo={saveOnboardingInfo}
            isLoading={isUserLoading}
          />
        )
      case STEP_WALLET:
        return (
          <div>
            <p className="mb-4">Please set up or confirm your secure wallet.</p>
            {user.hasWallet ? (
              <p className="text-green-500 mb-4">Wallet is already set up!</p>
            ) : (
              <CreateWalletModal onComplete={handleNext}>
                <Button data-testid="setup-wallet-button" className="w-full">
                  Setup Secure Wallet
                </Button>
              </CreateWalletModal>
            )}
            {!user.hasWallet && (
              <p className="text-xs text-gray-500 mt-2">
                After setting up your wallet, you will proceed to the next step.
              </p>
            )}
          </div>
        )
      case STEP_SUCCESS:
        return <SuccessScreen {...commonStepProps} />
      default:
        return <div>Unknown Step</div>
    }
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-16">
      <div className="w-full max-w-2xl mx-auto bg-white border border-gray-200 rounded-lg shadow-lg px-6 sm:px-8 py-8">
        {currentStep > 1 && (
          <div className="mb-4 sm:mb-6">
            <Button
              variant="ghost"
              onClick={handleBack}
              className="text-sm text-gray-600 hover:text-gray-800 px-0"
            >
              Back
            </Button>
          </div>
        )}

        {currentStep > 1 && getStepTitle(currentStep, stepTitles) && (
          <h1 className="text-2xl sm:text-3xl font-semibold text-gray-800 text-center mb-6 sm:mb-8">
            {getStepTitle(currentStep, stepTitles)}
          </h1>
        )}

        <div className="w-full">{renderStepContent()}</div>

        <div className="mt-8 w-full">
          <ProgressBar currentStep={currentStep} totalSteps={totalSteps} />
        </div>
      </div>
    </div>
  )
}
